import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { MapPin, Trash2, Loader2, Plus, Home, Search, Map as MapIcon } from "lucide-react";
import {
  listSavedPlaces,
  saveSavedPlace,
  deleteSavedPlace,
  setDefaultSavedPlace,
  searchAddresses,
} from "@/lib/addresses.functions";
import { LocalityCombobox, LocalityHelp } from "@/components/LocalityCombobox";
import { isValidLocality, searchLocalities } from "@/lib/swedish-localities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { savedPlacesQuery } from "@/lib/queries";

const LocationPickerMap = lazy(() => import("@/components/LocationPickerMap"));

export const Route = createFileRoute("/_authenticated/adresser")({
  head: () => ({
    meta: [
      { title: "Mina adresser — Sellavo" },
      {
        name: "description",
        content: "Spara dina vanligaste adresser och sätt en hemadress för sortering nära dig.",
      },
      { property: "og:title", content: "Mina adresser — Sellavo" },
      {
        property: "og:description",
        content: "Spara dina vanligaste adresser och sätt en hemadress för sortering nära dig.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Addresses,
});

type Suggestion = {
  label: string;
  street: string;
  city: string;
  latitude: number;
  longitude: number;
};

function Addresses() {
  const queryClient = useQueryClient();
  const savePlace = useServerFn(saveSavedPlace);
  const removePlace = useServerFn(deleteSavedPlace);
  const makeDefault = useServerFn(setDefaultSavedPlace);
  const lookup = useServerFn(searchAddresses);

  const [label, setLabel] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(false);

  // Debouncad adress-autocomplete mot OpenStreetMap.
  useEffect(() => {
    if (picked || address.trim().length < 4) {
      setSuggestions([]);
      return;
    }
    const q = [address.trim(), location].filter(Boolean).join(", ");
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = (await lookup({ data: { query: q } })) as Suggestion[];
        setSuggestions(res);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [address, location, picked, lookup]);

  const { data: places, isLoading } = useQuery(savedPlacesQuery());

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["saved-places"] });
    queryClient.invalidateQueries({ queryKey: ["ads"] });
  };

  const applySuggestion = (s: Suggestion) => {
    setPicked(true);
    setAddress(s.street || s.label.split(",")[0]);
    setCoords({ latitude: s.latitude, longitude: s.longitude });
    setSuggestions([]);
    if (!location && s.city) {
      const match = searchLocalities(s.city, 1)[0];
      if (match) setLocation(match.name);
    }
  };

  const save = useMutation({
    mutationFn: () =>
      savePlace({
        data: {
          label: label.trim(),
          location,
          address,
          isDefault,
          latitude: coords?.latitude,
          longitude: coords?.longitude,
        },
      }),
    onSuccess: () => {
      setLabel("");
      setLocation("");
      setAddress("");
      setCoords(null);
      setIsDefault(false);
      setShowMap(false);
      setPicked(false);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Kunde inte spara adressen"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removePlace({ data: { id } }),
    onSuccess: invalidate,
  });
  const setHome = useMutation({
    mutationFn: (id: string) => makeDefault({ data: { id } }),
    onSuccess: invalidate,
  });

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
        Mina adresser
      </h1>
      <p className="mt-2 text-muted-foreground">
        Spara platser du säljer från. Den adress du markerar som hemadress används när du sorterar
        annonser på ”Nära mig”.
      </p>

      <form
        className="mt-8 space-y-4 rounded-lg border border-border bg-card p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!label.trim()) return setError("Ge adressen ett namn, t.ex. Hemma.");
          if (!isValidLocality(location)) return setError("Välj en ort i listan.");
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="label">Namn på adressen</Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="T.ex. Hemma"
              maxLength={60}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="loc">Ort</Label>
              <LocalityHelp />
            </div>
            <LocalityCombobox id="loc" value={location} onChange={setLocation} />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="addr">Gatuadress</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="addr"
              className="pl-9"
              value={address}
              onChange={(e) => {
                setPicked(false);
                setAddress(e.target.value);
              }}
              placeholder="Börja skriv, t.ex. Storgatan 1"
              maxLength={160}
              autoComplete="off"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
            {suggestions.length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md">
                {suggestions.map((s) => (
                  <li key={`${s.latitude}-${s.longitude}-${s.label}`}>
                    <button
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="flex w-full items-start gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0">{s.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {coords && (
            <p className="text-xs text-muted-foreground">
              Position vald: {coords.latitude.toFixed(4)}, {coords.longitude.toFixed(4)}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 cursor-pointer accent-[hsl(var(--primary))]"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Använd som hemadress för ”Nära mig”
          </label>
          <Button type="button" variant="outline" size="sm" onClick={() => setShowMap((v) => !v)}>
            <MapIcon className="h-4 w-4" /> {showMap ? "Dölj karta" : "Välj plats på karta"}
          </Button>
        </div>

        {showMap && (
          <Suspense
            fallback={
              <div className="flex h-72 items-center justify-center rounded-md border border-border bg-muted text-sm text-muted-foreground">
                Laddar karta…
              </div>
            }
          >
            <div className="space-y-1">
              <LocationPickerMap
                latitude={coords?.latitude ?? null}
                longitude={coords?.longitude ?? null}
                onPick={setCoords}
              />
              <p className="text-xs text-muted-foreground">
                Klicka på kartan för att finjustera positionen.
              </p>
            </div>
          </Suspense>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}{" "}
          Spara adress
        </Button>
      </form>

      <div className="mt-8 space-y-3">
        {isLoading ? (
          <p className="text-muted-foreground">Laddar…</p>
        ) : (places?.length ?? 0) === 0 ? (
          <p className="text-muted-foreground">Du har inga sparade adresser ännu.</p>
        ) : (
          places!.map((p) => (
            <div
              key={p.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                  <MapPin className="h-4 w-4 text-primary" /> {p.label}
                  {p.is_default && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      <Home className="h-3 w-3" /> Hemadress
                    </span>
                  )}
                </p>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {[p.address, p.location].filter(Boolean).join(", ")}
                  {p.latitude == null && " · saknar position"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!p.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={setHome.isPending}
                    onClick={() => setHome.mutate(p.id)}
                  >
                    <Home className="h-4 w-4" /> Sätt som hem
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ta bort ${p.label}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(p.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </main>
  );
}
